import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Flame, Sparkles, Clock, CheckCircle2 } from "lucide-react";

export default function Saadhna() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => { api.get("/saadhnas").then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);

  const enroll = async () => {
    if (!user) { window.location.href = "/login"; return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/saadhnas/${selected.id}/enroll`);
      setReceipt(data);
      toast.success("Saadhna sankalpa taken");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const close = () => { setSelected(null); setReceipt(null); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 text-[#B45309] mb-2">
          <Sparkles className="h-4 w-4" />
          <span className="text-xs uppercase tracking-[0.2em]">Guided saadhna paths</span>
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#5C061C]">{t("saadhna.title")}</h1>
        <p className="mt-2 text-[#6E6259] max-w-2xl">{t("saadhna.subtitle")}</p>
      </div>

      {!items ? (
        <p className="text-[#6E6259]">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-[#6E6259]">No saadhnas available.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((s) => (
            <div key={s.id} className="card-warm rounded-2xl overflow-hidden flex flex-col" data-testid={`saadhna-card-${s.id}`}>
              <div className="relative h-56 bg-[#F4EEDF]">
                {s.image_url && <img src={s.image_url} alt={s.deity} className="w-full h-full object-cover" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-3 left-4 right-4 text-white">
                  <p className="text-xs uppercase tracking-widest opacity-90">Deity</p>
                  <p className="font-serif text-xl leading-tight">{s.deity}</p>
                </div>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="font-serif text-xl text-[#5C061C]">{s.title}</h3>
                <p className="text-sm text-[#6E6259] mt-2 flex-1">{s.description}</p>
                <div className="flex items-center gap-2 text-sm text-[#6E6259] mt-3">
                  <Clock className="h-3.5 w-3.5" /> {s.duration_days} days · access to daily guidance
                </div>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-2xl font-serif text-[#5C061C]">₹{s.price}</span>
                  <Button onClick={() => setSelected(s)} className="btn-saffron rounded-full h-11 px-5" data-testid={`saadhna-start-${s.id}`}>
                    <Flame className="h-4 w-4 mr-2" /> {t("saadhna.start")}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="sm:max-w-lg bg-[#FAF7F2] border-[#E8DEC8]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#5C061C]">{selected?.title}</DialogTitle>
          </DialogHeader>
          {receipt ? (
            <div className="text-center py-4">
              <CheckCircle2 className="h-14 w-14 text-[#15803D] mx-auto" />
              <h3 className="font-serif text-2xl text-[#5C061C] mt-3">{t("saadhna.enrolled")}</h3>
              <p className="text-[#6E6259] mt-1">The complete guidance is now available in your Profile → My Saadhnas.</p>
              <p className="text-xs text-[#6E6259] mt-2">Receipt: <span className="font-mono">{receipt.receipt_id}</span></p>
              <div className="flex gap-2 justify-center mt-4">
                <Link to="/profile"><Button className="btn-saffron rounded-full h-11 px-6">Open My Saadhnas</Button></Link>
                <Button variant="outline" onClick={close} className="rounded-full h-11">{t("common.close")}</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-[#6E6259]">{selected?.description}</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-lg bg-white border border-[#E8DEC8] p-3"><p className="text-xs text-[#B45309] uppercase tracking-widest">Duration</p><p className="font-semibold text-[#5C061C]">{selected?.duration_days} days</p></div>
                <div className="rounded-lg bg-white border border-[#E8DEC8] p-3"><p className="text-xs text-[#B45309] uppercase tracking-widest">Contribution</p><p className="font-semibold text-[#5C061C]">₹{selected?.price}</p></div>
              </div>
              <p className="text-xs text-[#6E6259]">On enrollment you'll receive the full mantra, daily routine, and step-by-step guidance in your profile for the entire duration. All payments are non-refundable.</p>
              <Button onClick={enroll} disabled={busy} className="btn-saffron w-full h-12 rounded-full" data-testid="saadhna-enroll-button">
                {busy ? "Processing..." : `${t("saadhna.enroll")} ₹${selected?.price}`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
